import { useEffect, useState } from 'react';
import { Box, Collapse, Stack, Typography } from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { MarkdownContent } from '../../../shared/components/MarkdownContent';
import type { EntityTypeEnrichmentSection } from '../../../core/types/data-models';

/** Collapse/expand-all broadcast from InsightTopicTab — `tick` changes on every toggle so repeats still fire. */
export type ExpandSignal = { value: boolean; tick: number };

/**
 * One heading of an insight topic — the title row toggles its Markdown body
 * and sub-sections, the same collapse/expand shape DocumentDetail's
 * SectionRow gives a document's sections.
 */
export function InsightTopicSectionRow({
  section,
  childrenByParent,
  depth,
  expandSignal,
}: {
  section: EntityTypeEnrichmentSection;
  childrenByParent: Map<number, EntityTypeEnrichmentSection[]>;
  depth: number;
  expandSignal: ExpandSignal | null;
}) {
  const children = childrenByParent.get(section.id) ?? [];
  const hasContent = !!section.content?.trim();
  const isExpandable = hasContent || children.length > 0;
  // Top-level sections start open so a topic reads at a glance whether it was
  // opened here or from the dashboard's Managed Knowledge block; deeper levels
  // start collapsed and follow the collapse/expand-all toggle like the rest.
  const [open, setOpen] = useState(depth === 0);

  useEffect(() => {
    if (expandSignal != null) setOpen(expandSignal.value);
  }, [expandSignal]);

  return (
    <Box>
      <Stack
        direction="row"
        alignItems="center"
        onClick={() => {
          if (isExpandable) setOpen((o) => !o);
        }}
        sx={{
          pl: depth * 2.5,
          py: depth === 0 ? 1 : 0.5,
          borderRadius: 1,
          cursor: isExpandable ? 'pointer' : 'default',
          '&:hover': isExpandable ? { bgcolor: 'action.hover' } : {},
        }}
      >
        <Box sx={{ width: 24, display: 'flex', alignItems: 'center', flexShrink: 0 }}>
          {isExpandable && (
            open
              ? <ExpandMoreIcon sx={{ fontSize: depth === 0 ? 18 : 15, color: 'var(--color-muted)' }} />
              : <ChevronRightIcon sx={{ fontSize: depth === 0 ? 18 : 15, color: 'var(--color-muted)' }} />
          )}
        </Box>
        <Typography
          sx={{
            flex: 1,
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            fontFamily: 'var(--font-sans)',
            fontSize: depth === 0 ? 'var(--font-size-xl)' : depth === 1 ? 16 : 'var(--font-size-base)',
            fontWeight: depth === 0 ? 700 : 600,
            letterSpacing: depth === 0 ? '-0.015em' : 'normal',
            lineHeight: 'var(--line-height-tight)',
            color: 'var(--color-ink)',
          }}
        >
          {section.title}
        </Typography>
      </Stack>

      <Collapse in={open} unmountOnExit>
        {hasContent && (
          <Box sx={{ pl: depth * 2.5 + 3, pr: 2, py: 1 }}>
            <MarkdownContent>{section.content ?? ''}</MarkdownContent>
          </Box>
        )}
        {children.map((child) => (
          <InsightTopicSectionRow
            key={child.id}
            section={child}
            childrenByParent={childrenByParent}
            depth={depth + 1}
            expandSignal={expandSignal}
          />
        ))}
      </Collapse>
    </Box>
  );
}
